import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { LinearProgress } from '@mui/material';
import LeadInformationForm from 'app/pages/loanApplication/tabs/lead/tabs/LeadInfo/LeadInformationForm';
import PropTypes from 'prop-types';

import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getLeadSourcesAction, getPropertiesAction } from 'store/companies/actions';
import { getLocationsCountiesAction } from 'store/loanApplications/actions';

/**
 * @param {Object} props
 * @param {boolean} [props.createAsLoan] - When true the form creates a lead then converts it to a loan.
 * @param {boolean} [props.isConvertLeadToLoan] - When true the form is seeded from the stored lead.
 */
const NewLeadModal = ({
  createAsLoan = false,
  isConvertLeadToLoan = false,
  onLoadingChange,
  ...props
}) => {
  const dispatch = useDispatch();

  const companyId = useCompanyId();

  // The form captures its values once, on mount, and a select only keeps a stored value that
  // matches a loaded option -- so a converted lead's County and Lead Source would come up blank if
  // the form mounted before these options arrived.
  const [areLookupsSettled, setAreLookupsSettled] = useState(!isConvertLeadToLoan);

  useEffectSafeStrictMode(() => {
    Promise.allSettled([
      dispatch(getLocationsCountiesAction()),
      dispatch(getLeadSourcesAction()),
      dispatch(getPropertiesAction({ companyId })),
    ]).then(() => setAreLookupsSettled(true));
  }, [dispatch, companyId]);

  // A host with an external submit button (the CRM convert drawer) must not offer it before the
  // form exists to receive the submit.
  useEffect(() => {
    if (areLookupsSettled) {
      return;
    }

    onLoadingChange?.(true);

    return () => onLoadingChange?.(false);
  }, [areLookupsSettled, onLoadingChange]);

  if (!areLookupsSettled) {
    return <LinearProgress className="w-full" color="primary" />;
  }

  return (
    <LeadInformationForm
      isCreateNewLead
      createAsLoan={createAsLoan}
      isConvertLeadToLoan={isConvertLeadToLoan}
      onLoadingChange={onLoadingChange}
      {...props}
    />
  );
};

NewLeadModal.propTypes = {
  createAsLoan: PropTypes.bool,
  isConvertLeadToLoan: PropTypes.bool,
  onLoadingChange: PropTypes.func,
};

export default NewLeadModal;
